import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from './ui/button';
import { Menu, X, ChevronDown, Calculator, LayoutDashboard, FileText, Activity, Phone, MapPin, Car, UserRound } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const Header = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [adminOpen, setAdminOpen] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const dropdownRef = useRef(null);
  const adminDropdownRef = useRef(null);

  useEffect(() => {
    fetch(`${API}/api/auth/me`, { credentials: 'include' })
      .then(r => { if (r.ok) setIsAdmin(true); })
      .catch(() => {});
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) setServicesOpen(false);
      if (adminDropdownRef.current && !adminDropdownRef.current.contains(event.target)) setAdminOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const isActive = (path) => location.pathname === path;
  const go = (path) => { navigate(path); setMobileMenuOpen(false); setServicesOpen(false); };

  return (
    <header className="bg-black text-white sticky top-0 z-50 border-b border-gray-800">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="flex items-center justify-between h-16 sm:h-20">
          {/* Logo — matches footer */}
          <div className="flex items-center cursor-pointer group shrink-0" onClick={() => go('/')} data-testid="header-logo">
            <img src="/assets/ace_logo_horizontal.webp" alt="ACE Logo" className="h-10 sm:h-12 w-auto object-contain" />
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden xl:flex items-center space-x-0.5">
            {/* Services Dropdown */}
            <div className="relative" ref={dropdownRef}>
              <button onClick={() => setServicesOpen(!servicesOpen)}
                className={`flex items-center text-xs font-semibold px-2.5 py-2 rounded-md transition-colors ${
                  ['/driven', '/driving', '/corporate', '/sales', '/rentals'].some(p => location.pathname.startsWith(p)) ? 'text-red-500' : 'hover:text-red-500'
                }`} data-testid="services-dropdown-btn">
                Services <ChevronDown className={`ml-0.5 h-3.5 w-3.5 transition-transform ${servicesOpen ? 'rotate-180' : ''}`} />
              </button>
              {servicesOpen && (
                <div className="absolute top-full left-0 mt-2 w-80 bg-black border border-gray-800 rounded-lg shadow-xl py-1 z-50">
                  <button onClick={() => go('/driven')} className="w-full text-left px-4 py-3 hover:bg-gray-900 transition-colors" data-testid="nav-driven">
                    <div className="font-semibold text-sm">Driven <span className="text-gray-500 font-normal">— Concierge Car Services</span></div>
                    <div className="text-[10px] text-gray-500 mt-0.5">Black SUVs, sedans, Sprinters, exotics</div>
                  </button>
                  <div className="ml-4 border-l border-gray-800">
                    <button onClick={() => go('/rates')} className="w-full text-left px-4 py-2.5 hover:bg-gray-900 transition-colors" data-testid="nav-rates">
                      <div className="text-xs font-medium text-gray-300">Rates <span className="text-gray-600">— Hourly & Transfer Pricing</span></div>
                    </button>
                    <button onClick={() => go('/driven/occasions')} className="w-full text-left px-4 py-2.5 hover:bg-gray-900 transition-colors" data-testid="nav-occasions">
                      <div className="text-xs font-medium text-gray-300">Occasions <span className="text-gray-600">— Full Catalog of Services</span></div>
                    </button>
                  </div>
                  <button onClick={() => go('/driving')} className="w-full text-left px-4 py-3 hover:bg-gray-900 transition-colors" data-testid="nav-driving">
                    <div className="font-semibold text-sm">Driving <span className="text-gray-500 font-normal">— Auto Consulting & More</span></div>
                    <div className="text-[10px] text-gray-500 mt-0.5">Consulting, acquisitions, fleet management</div>
                  </button>
                  <div className="ml-4 border-l border-gray-800">
                    <button onClick={() => go('/sales')} className="w-full text-left px-4 py-2.5 hover:bg-gray-900 transition-colors" data-testid="nav-sales">
                      <div className="text-xs font-medium text-gray-300">Sales <span className="text-gray-600">— Auctions & Lease to Own</span></div>
                    </button>
                    <button onClick={() => go('/rentals')} className="w-full text-left px-4 py-2.5 hover:bg-gray-900 transition-colors" data-testid="nav-rentals">
                      <div className="text-xs font-medium text-gray-300">Rentals <span className="text-gray-600">— Self-Drive Fleet</span></div>
                    </button>
                  </div>
                  <div className="border-t border-gray-800 my-1" />
                  <button onClick={() => go('/corporate')} className="w-full text-left px-4 py-3 hover:bg-gray-900 transition-colors" data-testid="nav-corporate">
                    <div className="font-semibold text-sm">Corporate</div>
                    <div className="text-[10px] text-gray-500 mt-0.5">Enterprise fleet & transportation solutions</div>
                  </button>
                </div>
              )}
            </div>

            <button onClick={() => go('/fleet-gallery')}
              className={`text-xs font-semibold px-2.5 py-2 rounded-md transition-colors ${isActive('/fleet-gallery') ? 'text-red-500' : 'hover:text-red-500'}`}
              data-testid="nav-fleet">
              Gallery
            </button>
            <button onClick={() => go('/membership')}
              className={`text-xs font-semibold px-2.5 py-2 rounded-md transition-colors ${isActive('/membership') ? 'text-red-500' : 'hover:text-red-500'}`}
              data-testid="nav-membership">
              Memberships
            </button>
            <button onClick={() => go('/blog')}
              className={`text-xs font-semibold px-2.5 py-2 rounded-md transition-colors ${isActive('/blog') ? 'text-red-500' : 'hover:text-red-500'}`}
              data-testid="nav-blog">
              Auto Lifestyle Journal
            </button>
            <button onClick={() => go('/about')}
              className={`text-xs font-semibold px-2.5 py-2 rounded-md transition-colors ${isActive('/about') ? 'text-red-500' : 'hover:text-red-500'}`}
              data-testid="nav-about">
              About
            </button>
            <button onClick={() => go('/contact')}
              className={`text-xs font-semibold px-2.5 py-2 rounded-md transition-colors ${isActive('/contact') ? 'text-red-500' : 'hover:text-red-500'}`}
              data-testid="nav-contact">
              Contact
            </button>
            <button onClick={() => go('/portal/login')}
              className={`text-xs font-semibold px-2.5 py-2 rounded-md transition-colors ${isActive('/portal') ? 'text-red-500' : 'text-red-500/70 hover:text-red-500'}`}
              data-testid="nav-client-portal">
              Client Portal
            </button>

            {/* Admin Dropdown — only visible when logged in */}
            {isAdmin && (
            <div className="relative" ref={adminDropdownRef}>
              <button onClick={() => setAdminOpen(!adminOpen)}
                className="flex items-center text-xs font-semibold text-gray-600 hover:text-red-500 transition-colors px-2.5 py-2 rounded-md"
                data-testid="admin-dropdown-btn">
                Admin <ChevronDown className={`ml-0.5 h-3.5 w-3.5 transition-transform ${adminOpen ? 'rotate-180' : ''}`} />
              </button>
              {adminOpen && (
                <div className="absolute top-full right-0 mt-2 w-56 bg-black border border-gray-800 rounded-lg shadow-xl py-1 z-50">
                  <button onClick={() => go('/admin')} className="w-full text-left px-4 py-2.5 hover:bg-gray-900 flex items-center gap-2.5" data-testid="nav-admin-dashboard">
                    <LayoutDashboard className="h-4 w-4 text-gray-400" /><span className="text-sm">Dashboard</span>
                  </button>
                  <button onClick={() => go('/command-center')} className="w-full text-left px-4 py-2.5 hover:bg-gray-900 flex items-center gap-2.5" data-testid="nav-command-center">
                    <Activity className="h-4 w-4 text-orange-500" /><span className="text-sm text-orange-500">Command Center</span>
                  </button>
                  <button onClick={() => go('/pricing-hq')} className="w-full text-left px-4 py-2.5 hover:bg-gray-900 flex items-center gap-2.5" data-testid="nav-pricing-hq">
                    <Calculator className="h-4 w-4 text-red-500" /><span className="text-sm text-red-500">Pricing Control HQ</span>
                  </button>
                  <button onClick={() => go('/cms-admin')} className="w-full text-left px-4 py-2.5 hover:bg-gray-900 flex items-center gap-2.5" data-testid="nav-cms-admin">
                    <FileText className="h-4 w-4 text-purple-500" /><span className="text-sm text-purple-500">Content Manager</span>
                  </button>
                  <button onClick={() => go('/admin/control-room')} className="w-full text-left px-4 py-2.5 hover:bg-gray-900 flex items-center gap-2.5" data-testid="nav-control-room">
                    <Activity className="h-4 w-4 text-teal-500" /><span className="text-sm text-teal-500">Control Room</span>
                  </button>
                  <button onClick={() => go('/admin/consulting')} className="w-full text-left px-4 py-2.5 hover:bg-gray-900 flex items-center gap-2.5" data-testid="nav-consulting-pipeline">
                    <MapPin className="h-4 w-4 text-amber-500" /><span className="text-sm text-amber-500">Consulting Pipeline</span>
                  </button>
                  <button onClick={() => go('/admin/rental-pricing')} className="w-full text-left px-4 py-2.5 hover:bg-gray-900 flex items-center gap-2.5" data-testid="nav-rental-pricing">
                    <Car className="h-4 w-4 text-green-500" /><span className="text-sm text-green-500">Rental Pricing</span>
                  </button>
                </div>
              )}
            </div>
            )}
          </nav>

          {/* Desktop CTA */}
          <div className="hidden xl:flex items-center space-x-2 shrink-0">
            <a href="tel:6788463009" className="flex items-center text-xs text-gray-400 hover:text-white transition-colors">
              <Phone className="h-3.5 w-3.5 mr-1" />(678) 846-3009
            </a>
            <button onClick={() => go('/portal/login')} className="flex items-center gap-1.5 text-xs text-gray-300 hover:text-white border border-gray-700 hover:border-gray-500 rounded px-3 h-9 transition-colors" data-testid="nav-signin-btn">
              <UserRound className="h-3.5 w-3.5" /> Sign In
            </button>
            <Button onClick={() => go('/booking')} className="bg-red-600 hover:bg-red-700 text-white text-xs h-9 px-4" data-testid="nav-book-btn">
              Book Now
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="xl:hidden text-white" data-testid="mobile-menu-btn">
            {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="xl:hidden py-3 border-t border-gray-800 max-h-[calc(100vh-4rem)] overflow-y-auto">
            <nav className="flex flex-col space-y-0.5">
              <p className="text-[9px] text-gray-600 uppercase tracking-widest px-3 py-1.5 font-bold">Services</p>
              <button onClick={() => go('/driven')} className="text-left text-sm font-medium hover:text-red-500 px-3 py-2.5 hover:bg-gray-900 rounded">
                Driven <span className="text-xs text-gray-500">— Concierge Car Services</span>
              </button>
              <div className="ml-4 border-l border-gray-800 pl-2 space-y-0.5">
                <button onClick={() => go('/rates')} className="text-left text-xs font-medium text-gray-400 hover:text-red-500 px-3 py-2 hover:bg-gray-900 rounded w-full">
                  Rates — Hourly & Transfer Pricing
                </button>
                <button onClick={() => go('/driven/occasions')} className="text-left text-xs font-medium text-gray-400 hover:text-red-500 px-3 py-2 hover:bg-gray-900 rounded w-full">
                  Occasions — Full Catalog of Services
                </button>
              </div>
              <button onClick={() => go('/driving')} className="text-left text-sm font-medium hover:text-red-500 px-3 py-2.5 hover:bg-gray-900 rounded">
                Driving <span className="text-xs text-gray-500">— Auto Consulting & More</span>
              </button>
              <div className="ml-4 border-l border-gray-800 pl-2 space-y-0.5">
                <button onClick={() => go('/sales')} className="text-left text-xs font-medium text-gray-400 hover:text-red-500 px-3 py-2 hover:bg-gray-900 rounded w-full">
                  Sales — Auctions & Lease to Own
                </button>
                <button onClick={() => go('/rentals')} className="text-left text-xs font-medium text-gray-400 hover:text-red-500 px-3 py-2 hover:bg-gray-900 rounded w-full">
                  Rentals — Self-Drive Fleet
                </button>
              </div>
              <button onClick={() => go('/corporate')} className="text-left text-sm font-medium hover:text-red-500 px-3 py-2.5 hover:bg-gray-900 rounded">
                Corporate
              </button>

              <div className="border-t border-gray-800 my-1.5" />
              <button onClick={() => go('/fleet-gallery')} className="text-left text-sm font-medium hover:text-red-500 px-3 py-2.5 hover:bg-gray-900 rounded">
                Fleet Gallery
              </button>
              <button onClick={() => go('/membership')} className="text-left text-sm font-medium hover:text-red-500 px-3 py-2.5 hover:bg-gray-900 rounded">
                Memberships
              </button>
              <button onClick={() => go('/blog')} className="text-left text-sm font-medium hover:text-red-500 px-3 py-2.5 hover:bg-gray-900 rounded">
                Auto Lifestyle Journal
              </button>
              <button onClick={() => go('/about')} className="text-left text-sm font-medium hover:text-red-500 px-3 py-2.5 hover:bg-gray-900 rounded">
                About
              </button>
              <button onClick={() => go('/contact')} className="text-left text-sm font-medium hover:text-red-500 px-3 py-2.5 hover:bg-gray-900 rounded">
                Contact
              </button>

              {isAdmin && (<>
              <div className="border-t border-gray-800 my-1.5" />
              <p className="text-[9px] text-gray-600 uppercase tracking-widest px-3 py-1.5 font-bold">Admin</p>
              <button onClick={() => go('/admin')} className="text-left text-sm font-medium hover:text-red-500 px-3 py-2 flex items-center gap-2">
                <LayoutDashboard className="h-4 w-4" /> Dashboard
              </button>
              <button onClick={() => go('/command-center')} className="text-left text-sm font-medium text-orange-500 hover:text-orange-400 px-3 py-2 flex items-center gap-2">
                <Activity className="h-4 w-4" /> Command Center
              </button>
              <button onClick={() => go('/pricing-hq')} className="text-left text-sm font-medium text-red-500 hover:text-red-400 px-3 py-2 flex items-center gap-2">
                <Calculator className="h-4 w-4" /> Pricing Control HQ
              </button>
              </>)}

              <div className="pt-3 px-3 space-y-2">
                <a href="tel:6788463009" className="flex items-center justify-center text-sm text-gray-400 py-2">
                  <Phone className="h-4 w-4 mr-2" /> (678) 846-3009
                </a>
                <button onClick={() => go('/portal/login')} className="flex items-center justify-center gap-2 w-full text-sm text-gray-300 border border-gray-700 rounded py-2.5 hover:border-gray-500" data-testid="mobile-signin-btn">
                  <UserRound className="h-4 w-4" /> Sign In
                </button>
                <Button onClick={() => go('/booking')} className="bg-red-600 hover:bg-red-700 text-white w-full">
                  Book Now
                </Button>
              </div>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
